import React, { memo } from 'react'
import { useMediaTypes, useMemo } from 'sb-hooks'
import cx from 'classnames'

import type { GridProps, GridMediaColumnsSize } from 'components/_new/layout'
import { Grid } from 'components/_new/layout'

import s from './Products.scss'


type Product = any
type InjectedItem = {
  order?: number | { [key: string]: number }
}

export const getOrderedItems = (items: Product[], injectItems: InjectedItem[] = [], mediaTypes) => {
  if (!items || !items.length) {
    return null
  }

  if (!injectItems.length) {
    return items
  }

  const orderedItems = [ ...items ]

  injectItems.forEach((item) => {
    // if item in injectItems has "order" we need to reorder "items"
    // if "order" is object with different media we generate array for current media type – descktop, mobile, etc.
    if (item.order && typeof item.order === 'object') {
      let order

      // get "order" for current media type
      Object.keys(item.order).some((media) => {
        if (mediaTypes[media]) {
          order = item.order[media]
          return true
        }

        return false
      })

      // if there is no order found for current screen we use first key value in "order" object
      if (!order) {
        const media = Object.keys(item.order)[0]

        order = item.order[media]
      }

      if (items.length >= order - 1) {
        orderedItems.splice(order - 1, 0, item)
      }
    }
    else if (typeof item.order === 'number' && item.order > 0 && items.length >= item.order - 1) {
      orderedItems.splice(item.order - 1, 0, item)
    }
    else {
      orderedItems.push(item)
    }
  })

  return orderedItems
}

export type ProductsProps = {
  children: (data: Product) => React.ReactNode
  items: Product[]
  className?: string
  productClassName?: string
  columns?: GridMediaColumnsSize
  gutter?: GridProps['gutter']
  alignItems?: GridProps['alignItems']
  fullHeight?: boolean
  withMargin?: boolean
  injectItems?: InjectedItem[]
  lastItemNode?: React.ReactNode
}

const Products: React.FunctionComponent<ProductsProps> = (props) => {
  const {
    children, className, columns, gutter, items,
    injectItems = [], productClassName, lastItemNode, alignItems, fullHeight = true, withMargin = true,
  } = props

  const mediaTypes = useMediaTypes()

  const productCellClassName = cx(productClassName, {
    [s.fullHeight]: fullHeight,
    [s.withMargin]: withMargin,
  })

  const productInjectedCellClassName = cx({
    [s.fullHeight]: fullHeight,
    [s.withMargin]: withMargin,
  })

  const gridColumns = useMemo(() => {
    const { mobile = 2, tabletPortrait = 2, tabletLandscape = 3, desktop = 4 } = columns || {}

    return { mobile, tabletPortrait, tabletLandscape, desktop }
  }, [ columns ])

  const orderedItems  = useMemo(() => (
    getOrderedItems(items, injectItems, mediaTypes)
  ), [ injectItems, items, mediaTypes ])

  if (!orderedItems) {
    return null
  }

  const listItems = orderedItems.map((item, index) => {
    // if item come from injectItems
    if (item.render) {
      return (
        <Grid.Cell key={item.id || index} size={item.size} className={productInjectedCellClassName}>
          {item.render()}
        </Grid.Cell>
      )
    }

    // if item is Product
    return (
      <Grid.Cell key={item.id || index} className={productCellClassName}>
        {children(item)}
      </Grid.Cell>
    )
  })

  // perfumesList class is needed for scrolling, because there can be multiple perfumes lists on the page
  return (
    <div
      data-testid="perfumesList"
      className={cx('perfumesList', s.products, className)}
    >
      <Grid columns={gridColumns} gutter={gutter} alignItems={alignItems}>
        {listItems}
        {lastItemNode}
      </Grid>
    </div>
  )
}


export default memo(Products)
